import { ChevronRight, Loader2, Lock } from "lucide-react";

import { cn } from "@/lib/utils";
import { useOnboardingQualification } from "@/hooks/queries/useOnboardingQualification";
import type { OnboardingQualification, QualificationVerdict } from "@/types/domain";
import { ActionBar, ActionButton, Eyebrow, ScreenTitle } from "../OnboardingShell";
import { departmentLabel } from "../lineHelpers";
import { closingLabel, type SaleCopy } from "./contractorLine";

const VERDICT: Record<
  QualificationVerdict | "PENDING",
  { label: string; badge: string; count: string }
> = {
  CALIFICA: { label: "Calificas", badge: "bg-blue-50 text-blue-700", count: "text-blue-700" },
  REVISAR: { label: "Me falta poco", badge: "bg-orange-50 text-orange-700", count: "text-orange-700" },
  NO_CALIFICA: { label: "No calificas", badge: "bg-red-50 text-red-700", count: "text-red-600" },
  PENDING: { label: "Por analizar", badge: "bg-slate-100 text-slate-500", count: "text-slate-500" },
};

type Work = OnboardingQualification["works"][number];

/**
 * El resultado del cruce de prueba: en cuántas de las licitaciones revisadas
 * califica, con el veredicto de cada una. Llegan regaladas: un clic abre su
 * detalle. Si alguna llega tapada, se ofrece desbloquearla.
 */
export function QualificationResultStep({
  sale,
  rubro,
  unlockingId,
  onUnlock,
  onOpenWork,
  onContinue,
}: {
  sale: SaleCopy;
  rubro: string;
  unlockingId: number | null;
  onUnlock: (contractingId: number, position: number) => void;
  onOpenWork: (contractingId: number) => void;
  /** `anyAnalyzed`: si alguna salió con veredicto, para entrar al dashboard con ellas. */
  onContinue: (anyAnalyzed: boolean) => void;
}) {
  const { data } = useOnboardingQualification();
  const works = data?.works ?? [];
  const count = (verdict: QualificationVerdict | null) =>
    works.filter((work) => work.verdict === verdict).length;
  const qualified = count("CALIFICA");
  const pending = count(null);

  if (works.length === 0) {
    return (
      <div className="mx-auto w-full max-w-[620px]">
        <Eyebrow tone="blue">Tu calificación</Eyebrow>
        <ScreenTitle>Todavía no hay {sale.many} de tu rubro para cruzar.</ScreenTitle>
        <p className="mt-4 text-[15px] leading-[1.6] text-slate-600">
          Necesitamos {sale.many} con las bases publicadas y el registro abierto. Tus documentos ya
          quedaron guardados.
        </p>
        <ActionBar>
          <ActionButton tone="blue" onClick={() => onContinue(false)} arrow>
            Continuar
          </ActionButton>
        </ActionBar>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[720px]">
      <Eyebrow tone="blue">Tu calificación · {rubro}</Eyebrow>
      <ScreenTitle>
        Calificas en <span className="text-blue-700">{qualified}</span> de las {works.length}{" "}
        {sale.many} que revisamos.
      </ScreenTitle>

      <div className="mt-6 grid grid-cols-3 gap-2.5">
        {(["CALIFICA", "REVISAR", "NO_CALIFICA"] as const).map((verdict) => (
          <div key={verdict} className="rounded-[16px] border border-slate-200 bg-white p-3.5">
            <p className={cn("text-[28px] font-extrabold leading-none", VERDICT[verdict].count)}>
              {count(verdict)}
            </p>
            <p className="mt-1.5 text-[13px] font-semibold text-slate-700">{VERDICT[verdict].label}</p>
          </div>
        ))}
      </div>
      {pending > 0 && (
        <p className="mt-2 text-[12.5px] text-slate-500">
          {pending} {pending === 1 ? `${sale.one} queda` : `${sale.many} quedan`} por analizar.
        </p>
      )}

      <ul className="mt-4 overflow-hidden rounded-[18px] border border-slate-200 bg-white">
        {works.map((work, index) => (
          <VerdictRow
            key={work.contractingId}
            work={work}
            unlocking={unlockingId === work.contractingId}
            onUnlock={() => onUnlock(work.contractingId, index)}
            onOpen={() => onOpenWork(work.contractingId)}
          />
        ))}
      </ul>

      <p className="mt-3 text-[13px] text-slate-500">
        Cada {sale.one} que desbloquees se analizará con tus documentos.
      </p>

      <ActionBar>
        <ActionButton tone="blue" onClick={() => onContinue(works.length > pending)} arrow>
          Continuar
        </ActionButton>
      </ActionBar>
    </div>
  );
}

/** Una licitación con su veredicto. Bloqueada ofrece desbloquearla; abierta, abre su detalle. */
function VerdictRow({
  work,
  unlocking,
  onUnlock,
  onOpen,
}: {
  work: Work;
  unlocking: boolean;
  onUnlock: () => void;
  onOpen: () => void;
}) {
  const verdict = VERDICT[work.verdict ?? "PENDING"];
  const closing = closingLabel(work.registrationEndsAt);
  // Con spans y no párrafos: abierta, la fila entera es un botón.
  const summary = (
    <>
      <span
        className={cn(
          "shrink-0 rounded px-2 py-1 font-mono text-[10.5px] font-semibold uppercase",
          verdict.badge,
        )}
      >
        {verdict.label}
      </span>
      <span className="min-w-0 flex-1">
        {work.isLocked ? (
          // Relleno difuminado: no es la entidad, solo marca dónde va.
          <span
            className="block select-none text-[14px] font-semibold text-slate-700 blur-[5px]"
            aria-hidden
          >
            Municipalidad Provincial de la obra
          </span>
        ) : (
          <span className="block text-[14px] font-semibold text-slate-900">{work.entity}</span>
        )}
        <span className="block text-[12.5px] text-slate-500">
          {work.department && departmentLabel(work.department)}
          {closing && (
            <>
              {work.department && " · "}
              <span className={cn("font-semibold", closing.className)}>{closing.label}</span>
            </>
          )}
        </span>
      </span>
    </>
  );

  if (!work.isLocked) {
    return (
      <li className="border-b border-slate-100 last:border-0">
        <button
          type="button"
          onClick={onOpen}
          className="flex w-full cursor-pointer items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-slate-50"
        >
          {summary}
          <ChevronRight className="size-4 shrink-0 text-slate-400" />
        </button>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 border-b border-slate-100 px-4 py-3.5 last:border-0">
      {summary}
      <button
        type="button"
        onClick={onUnlock}
        disabled={unlocking}
        aria-label="Desbloquear · 1 vista"
        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-blue-200 px-3 py-1.5 text-[12.5px] font-bold text-blue-700 hover:bg-blue-50 disabled:cursor-wait disabled:opacity-70"
      >
        {unlocking ? <Loader2 className="size-3.5 animate-spin" /> : <Lock className="size-3.5" />}
        1 vista
      </button>
    </li>
  );
}
